<template>
<div>
    <div class="container-fluid">
        <div class="row">
            <div class="col-sm-12 col-xl-12" style="margin-top: 20px;">
                <div class="card">
                    <div class="card-header admin-header">
                        <div>
                            <h3>Administración</h3>
                            <p class="admin-subtitle">Cuentas, usuarios, RFCs y paquetes</p>
                        </div>
                        <button class="btn btn-outline-primary btn-sm" :disabled="loading" @click="loadData">
                            {{ loading ? 'Actualizando...' : 'Actualizar' }}
                        </button>
                    </div>
                    <div class="card-body">
                        <div v-if="error" class="alert alert-danger" role="alert">{{ error }}</div>

                        <div class="admin-search-wrap mb-3">
                            <input
                                v-model="searchQuery"
                                type="text"
                                class="form-control form-control-sm"
                                placeholder="Buscar cuenta por nombre, correo o WhatsApp"
                            >
                        </div>

                        <div class="table-responsive" v-if="filteredRows.length">
                            <table class="table table-lg table-hover align-middle admin-accounts-table">
                                <thead>
                                    <tr>
                                        <th>Cuenta</th>
                                        <th>Fecha registro</th>
                                        <th>Acceso social</th>
                                        <th>Última actividad</th>
                                        <th>Usuarios</th>
                                        <th>RFCs</th>
                                        <th>CFDIs</th>
                                        <th style="min-width: 250px;">Paquete</th>
                                        <th>Vigencia</th>
                                        <th>Próximo pago</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    <tr v-for="item in filteredRows" :key="item.id">
                                        <td>
                                            <div>
                                                <div class="fw-bold">{{ item.name }}</div>
                                                <small class="text-muted account-email">{{ item.email }}</small>
                                                <small v-if="item.whatsapp_phone_number" class="text-muted account-whatsapp">
                                                    WhatsApp: {{ item.whatsapp_phone_number }}
                                                </small>
                                                <div class="d-flex gap-1 account-actions mt-2">
                                                    <button
                                                        class="btn btn-outline-primary btn-sm"
                                                        :disabled="impersonating[item.id] || loading"
                                                        @click="impersonateAccount(item)"
                                                        title="Acceder temporalmente"
                                                        aria-label="Acceder temporalmente"
                                                    >
                                                        <i class="fa fa-sign-in"></i>
                                                        {{ impersonating[item.id] ? 'Accediendo...' : 'Acceder' }}
                                                    </button>
                                                    <button
                                                        class="btn btn-sm"
                                                        v-if="item.whatsapp_phone_number"
                                                        :class="whatsappButtonClass(item)"
                                                        :disabled="whatsappConversationLoading"
                                                        @click="openWhatsappConversationModal(item)"
                                                        title="Ver conversación de WhatsApp"
                                                        aria-label="Ver conversación de WhatsApp"
                                                    >
                                                        <i class="fa fa-whatsapp" :class="whatsappIconClass(item)"></i>
                                                    </button>
                                                    <button
                                                        class="btn btn-outline-warning btn-sm"
                                                        :disabled="passwordModalLoading || passwordModalGenerating"
                                                        @click="openPasswordModal(item)"
                                                        title="Generar contraseña"
                                                        aria-label="Generar contraseña"
                                                    >
                                                        <i class="fa fa-key"></i>
                                                    </button>
                                                    <button
                                                        class="btn btn-outline-danger btn-sm"
                                                        v-if="!item.is_delete_protected"
                                                        @click="openDeleteModal(item)"
                                                        title="Eliminar cuenta"
                                                        aria-label="Eliminar cuenta"
                                                    >
                                                        <i class="fa fa-trash"></i>
                                                    </button>
                                                    <button
                                                        class="btn btn-primary btn-sm"
                                                        v-else
                                                        type="button"
                                                        title="Cuenta segura, no se puede eliminar"
                                                        aria-label="Cuenta segura, no se puede eliminar"
                                                    >
                                                        <i class="fa fa-lock"></i>
                                                    </button>
                                                </div>
                                            </div>
                                        </td>
                                        <td>{{ formatDateTime(item.created_at) }}</td>
                                        <td>
                                            <div v-if="socialBadges(item).length" class="social-badges-group">
                                                <span
                                                    v-for="(badge, index) in socialBadges(item)"
                                                    :key="`${item.id}-social-${index}-${badge.type}`"
                                                    class="badge social-badge"
                                                    :class="`social-badge-${badge.type}`"
                                                >
                                                    {{ badge.text }}
                                                </span>
                                            </div>
                                            <span v-else>-</span>
                                        </td>
                                        <td>{{ formatDateTime(item.last_activity_at) }}</td>
                                        <td>{{ item.users_count || 0 }}</td>
                                        <td>{{ item.rfcs_count || 0 }}</td>
                                        <td>{{ item.cfdis_count || 0 }}</td>
                                        <td>
                                            <div class="d-flex gap-2">
                                                <select class="form-select form-select-sm" v-model="selectedPackages[item.id]">
                                                    <option v-for="pkg in packages" :key="pkg.id" :value="pkg.id">
                                                        {{ packageLabel(pkg) }}
                                                    </option>
                                                </select>
                                                <button
                                                    class="btn btn-primary btn-sm"
                                                    :disabled="saving[item.id] || !selectedPackages[item.id] || Number(selectedPackages[item.id]) === Number(item.package_id)"
                                                    @click="changePackage(item)"
                                                >
                                                    {{ saving[item.id] ? 'Guardando...' : 'Guardar' }}
                                                </button>
                                            </div>
                                        </td>
                                        <td>
                                            <div class="d-flex gap-2">
                                                <input
                                                    type="date"
                                                    class="form-control form-control-sm"
                                                    :value="currentValidityDate(item)"
                                                    @input="onValidityInput(item.id, $event.target.value)"
                                                >
                                                <button
                                                    class="btn btn-outline-primary btn-sm"
                                                    :disabled="savingValidity[item.id] || !currentValidityDate(item) || currentValidityDate(item) === toDateInput(item.ends_at)"
                                                    @click="changeValidity(item)"
                                                >
                                                    {{ savingValidity[item.id] ? 'Guardando...' : 'Guardar' }}
                                                </button>
                                            </div>
                                        </td>
                                        <td>{{ formatNextPayment(item) }}</td>
                                    </tr>
                                </tbody>
                            </table>
                        </div>

                        <div v-else-if="!loading" class="text-center py-4">
                            <h6>No hay cuentas para mostrar.</h6>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    </div>

    <div v-if="deleteModalVisible" class="modal fade show d-block" tabindex="-1" role="dialog" aria-modal="true">
        <div class="modal-dialog" role="document">
            <div class="modal-content">
                <div class="modal-header">
                    <h5 class="modal-title">Eliminar Cuenta</h5>
                    <button type="button" class="btn-close" aria-label="Close" @click="closeDeleteModal"></button>
                </div>
                <div class="modal-body">
                    <div v-if="deleteModalError" class="alert alert-danger" role="alert">{{ deleteModalError }}</div>

                    <div v-if="deleteModalLoading" class="text-muted">Cargando información de la cuenta...</div>

                    <div v-else-if="deleteSummary">
                        <p class="mb-2"><strong>Cuenta:</strong> {{ deleteSummary.name || '-' }}</p>
                        <p class="mb-2"><strong>Email:</strong> {{ deleteSummary.email || '-' }}</p>
                        <p class="mb-2"><strong>Usuarios:</strong> {{ deleteSummary.users_count || 0 }}</p>
                        <p class="mb-2"><strong>RFCs cargados:</strong> {{ deleteSummary.rfcs_count || 0 }}</p>
                        <p class="mb-2"><strong>CFDIs cargados:</strong> {{ deleteSummary.cfdis_count || 0 }}</p>
                        <p class="mb-3"><strong>Requests cargados:</strong> {{ deleteSummary.requests_count || 0 }}</p>

                        <label class="form-label" for="delete-confirmation-word">Escribe <strong>ELIMINAR</strong> para confirmar</label>
                        <input
                            id="delete-confirmation-word"
                            v-model="deleteConfirmationWord"
                            type="text"
                            class="form-control"
                            placeholder="ELIMINAR"
                            autocomplete="off"
                        >
                    </div>
                </div>
                <div class="modal-footer">
                    <button type="button" class="btn btn-secondary" :disabled="deleteModalDeleting" @click="closeDeleteModal">Cancelar</button>
                    <button
                        type="button"
                        class="btn btn-danger"
                        :disabled="deleteModalDeleting || deleteModalLoading || !deleteSummary || !isDeletionWordValid()"
                        @click="confirmDeleteAccount"
                    >
                        {{ deleteModalDeleting ? 'Eliminando...' : 'Eliminar cuenta' }}
                    </button>
                </div>
            </div>
        </div>
    </div>
    <div v-if="deleteModalVisible" class="modal-backdrop fade show"></div>

    <div v-if="whatsappConversationVisible" class="modal fade show d-block" tabindex="-1" role="dialog" aria-modal="true">
        <div class="modal-dialog modal-xl" role="document">
            <div class="modal-content whatsapp-conversation-modal">
                <div class="modal-header">
                    <h5 class="modal-title">Conversación WhatsApp</h5>
                    <button type="button" class="btn-close" aria-label="Close" @click="closeWhatsappConversationModal"></button>
                </div>
                <div class="modal-body">
                    <div class="whatsapp-conversation-account mb-3">
                        <div><strong>Cuenta:</strong> {{ whatsappConversationAccount?.name || '-' }}</div>
                        <div><strong>Email:</strong> {{ whatsappConversationAccount?.email || '-' }}</div>
                        <div><strong>WhatsApp:</strong> {{ whatsappConversationContact?.phone_number || '-' }}</div>
                    </div>

                    <div v-if="whatsappConversationError" class="alert alert-danger" role="alert">{{ whatsappConversationError }}</div>
                    <div v-if="whatsappConversationLoading" class="text-muted">Cargando conversación...</div>

                    <template v-else>
                        <div class="whatsapp-conversation-list">
                            <div class="text-center mb-2" v-if="whatsappConversationHasMore">
                                <button
                                    type="button"
                                    class="btn btn-outline-secondary btn-sm"
                                    :disabled="whatsappConversationLoadingOlder"
                                    @click="loadOlderWhatsappMessages"
                                >
                                    {{ whatsappConversationLoadingOlder ? 'Cargando...' : 'Cargar anteriores' }}
                                </button>
                            </div>
                            <div v-if="!whatsappConversationMessages.length" class="text-center text-muted py-4">
                                No hay mensajes registrados para esta cuenta.
                            </div>
                            <div
                                v-for="message in whatsappConversationMessages"
                                :key="message.id"
                                class="whatsapp-message-row"
                                :class="message.direction === 'outbound' ? 'is-outbound' : 'is-inbound'"
                            >
                                <div class="whatsapp-message-bubble">
                                    <div class="whatsapp-message-meta">
                                        <span>{{ message.direction === 'outbound' ? 'TusCFDIs' : (message.profile_name || 'Cliente') }}</span>
                                        <span>{{ formatDateTime(message.received_at || message.created_at) }}</span>
                                    </div>
                                    <div class="whatsapp-message-body">{{ message.message_body || '(sin contenido)' }}</div>
                                    <div class="whatsapp-message-status" v-if="message.direction === 'outbound'">
                                        <template v-if="whatsappStatusMeta(message).kind === 'ticks'">
                                            <span
                                                class="whatsapp-status-ticks"
                                                :style="{ color: whatsappStatusMeta(message).color }"
                                            >
                                                {{ whatsappStatusMeta(message).ticksText }}
                                            </span>
                                        </template>
                                        <template v-else>
                                            <i
                                                class="fa"
                                                :class="whatsappStatusMeta(message).iconClass"
                                                :style="{ color: whatsappStatusMeta(message).color }"
                                                aria-hidden="true"
                                            ></i>
                                        </template>
                                        <span>{{ whatsappStatusMeta(message).label }}</span>
                                    </div>
                                </div>
                            </div>
                        </div>

                        <div class="whatsapp-reply-box mt-3">
                            <label class="form-label" for="admin-whatsapp-template">Enviar template</label>
                            <div class="d-flex gap-2 align-items-center">
                                <select
                                    id="admin-whatsapp-template"
                                    v-model="whatsappTemplateSelected"
                                    class="form-select form-select-sm"
                                >
                                    <option value="">Selecciona un mensaje automático</option>
                                    <option
                                        v-for="template in whatsappTemplateOptions"
                                        :key="template.key"
                                        :value="template.key"
                                    >
                                        {{ template.label }}
                                    </option>
                                </select>
                                <button
                                    type="button"
                                    class="btn btn-primary btn-sm"
                                    :disabled="whatsappTemplateSending || !canSendWhatsappTemplate"
                                    @click="sendWhatsappTemplate"
                                >
                                    {{ whatsappTemplateSending ? 'Enviando...' : 'Enviar template' }}
                                </button>
                            </div>
                            <small class="text-muted d-block mt-2">
                                Los templates pueden enviarse manualmente desde este admin.
                            </small>
                        </div>

                        <div class="whatsapp-reply-box mt-3">
                            <label class="form-label" for="admin-whatsapp-reply">Responder manualmente</label>
                            <textarea
                                id="admin-whatsapp-reply"
                                v-model="whatsappReplyDraft"
                                class="form-control"
                                rows="4"
                                maxlength="1500"
                                placeholder="Escribe una respuesta para el cliente"
                                :disabled="!whatsappCanSendDirect"
                            ></textarea>
                            <small v-if="!whatsappCanSendDirect" class="text-warning d-block mt-2">
                                El mensaje directo solo está disponible dentro de las 24 horas posteriores al último mensaje entrante del cliente.
                            </small>
                            <div class="whatsapp-reply-actions mt-2">
                                <small class="text-muted">{{ whatsappReplyDraft.length }}/1500</small>
                                <button
                                    type="button"
                                    class="btn btn-success btn-sm"
                                    :disabled="whatsappReplySending || !canSendWhatsappReply"
                                    @click="sendWhatsappReply"
                                >
                                    {{ whatsappReplySending ? 'Enviando...' : 'Enviar por WhatsApp' }}
                                </button>
                            </div>
                        </div>
                    </template>
                </div>
                <div class="modal-footer">
                    <button type="button" class="btn btn-secondary" @click="closeWhatsappConversationModal">Cerrar</button>
                </div>
            </div>
        </div>
    </div>
    <div v-if="whatsappConversationVisible" class="modal-backdrop fade show"></div>

    <div v-if="passwordModalVisible" class="modal fade show d-block" tabindex="-1" role="dialog" aria-modal="true">
        <div class="modal-dialog" role="document">
            <div class="modal-content">
                <div class="modal-header">
                    <h5 class="modal-title">Generar nueva contraseña</h5>
                    <button type="button" class="btn-close" aria-label="Close" @click="closePasswordModal"></button>
                </div>
                <div class="modal-body">
                    <div v-if="passwordModalError" class="alert alert-danger" role="alert">{{ passwordModalError }}</div>

                    <template v-if="passwordModalTarget && !passwordModalGeneratedPassword">
                        <p class="mb-2">
                            Se generará una nueva contraseña segura para el usuario principal de esta cuenta.
                        </p>
                        <p class="mb-2"><strong>Cuenta:</strong> {{ passwordModalTarget.name || '-' }}</p>
                        <p class="mb-0"><strong>Email cuenta:</strong> {{ passwordModalTarget.email || '-' }}</p>
                    </template>

                    <template v-if="passwordModalGeneratedPassword">
                        <div class="alert alert-success" role="alert">
                            Contraseña generada correctamente.
                        </div>
                        <p class="mb-2"><strong>Usuario:</strong> {{ passwordModalGeneratedUser?.name || '-' }}</p>
                        <p class="mb-2"><strong>Email:</strong> {{ passwordModalGeneratedUser?.email || '-' }}</p>
                        <label class="form-label" for="generated-password-view"><strong>Nueva contraseña</strong></label>
                        <input
                            id="generated-password-view"
                            class="form-control"
                            :value="passwordModalGeneratedPassword"
                            readonly
                        >
                        <small class="text-muted d-block mt-2">Compártela con el cliente y sugiere cambiarla al iniciar sesión.</small>
                    </template>
                </div>
                <div class="modal-footer">
                    <button type="button" class="btn btn-secondary" :disabled="passwordModalGenerating" @click="closePasswordModal">
                        {{ passwordModalGeneratedPassword ? 'Cerrar' : 'Cancelar' }}
                    </button>
                    <button
                        v-if="!passwordModalGeneratedPassword"
                        type="button"
                        class="btn btn-warning"
                        :disabled="passwordModalGenerating || !passwordModalTarget"
                        @click="generatePasswordForAccount"
                    >
                        {{ passwordModalGenerating ? 'Generando...' : 'Confirmar y generar' }}
                    </button>
                </div>
            </div>
        </div>
    </div>
    <div v-if="passwordModalVisible" class="modal-backdrop fade show"></div>
</div>
</template>

<script>
import accountService from '@/services/account';

export default {
    data() {
        return {
            loading: false,
            error: '',
            rows: [],
            searchQuery: '',
            packages: [],
            selectedPackages: {},
            saving: {},
            selectedValidityDates: {},
            savingValidity: {},
            impersonating: {},
            deleteModalVisible: false,
            deleteModalLoading: false,
            deleteModalDeleting: false,
            deleteModalError: '',
            deleteSummary: null,
            deleteTargetId: null,
            deleteConfirmationWord: '',
            whatsappConversationVisible: false,
            whatsappConversationLoading: false,
            whatsappConversationError: '',
            whatsappConversationAccount: null,
            whatsappConversationContact: null,
            whatsappConversationMessages: [],
            whatsappConversationAccountId: null,
            whatsappConversationHasMore: false,
            whatsappConversationNextBeforeId: null,
            whatsappConversationLoadingOlder: false,
            whatsappReplyDraft: '',
            whatsappReplySending: false,
            whatsappCanSendDirect: false,
            whatsappTemplateOptions: [],
            whatsappTemplateSelected: '',
            whatsappTemplateSending: false,
            passwordModalVisible: false,
            passwordModalLoading: false,
            passwordModalGenerating: false,
            passwordModalError: '',
            passwordModalTarget: null,
            passwordModalGeneratedPassword: '',
            passwordModalGeneratedUser: null,
        }
    },
    mounted: async function(){
        await this.loadData();
    },
    methods: {
        ensureAdminAccess() {
            const rawUser = localStorage.getItem('user');
            if (!rawUser || rawUser === 'undefined') {
                this.$router.replace({ name: 'dashboard' });
                return false;
            }

            try {
                const user = JSON.parse(rawUser) || {};
                if (!Boolean(user.is_admin)) {
                    this.$router.replace({ name: 'dashboard' });
                    return false;
                }
            } catch (error) {
                this.$router.replace({ name: 'dashboard' });
                return false;
            }

            return true;
        },
        async loadData(){
            if (!this.ensureAdminAccess()) {
                return;
            }

            this.loading = true;
            this.error = '';

            try{
                const response = await accountService.getAdminAccountsSummary();
                if(response && response.type === 'success'){
                    this.rows = Array.isArray(response?.data?.accounts) ? response.data.accounts : [];
                    this.packages = Array.isArray(response?.data?.packages) ? response.data.packages : [];

                    const nextMap = {};
                    const validityMap = {};
                    this.rows.forEach((item) => {
                        nextMap[item.id] = Number(item.package_id || 0) || null;
                        validityMap[item.id] = this.toDateInput(item.ends_at);
                    });
                    this.selectedPackages = nextMap;
                    this.selectedValidityDates = validityMap;
                    return;
                }

                this.rows = [];
                this.error = response?.message || 'No fue posible cargar el listado de cuentas.';
            }
            catch(error){
                this.rows = [];
                this.error = error?.response?.data?.message || 'No fue posible cargar el listado de cuentas.';
            }
            finally{
                this.loading = false;
            }
        },
        async changePackage(item){
            const packageId = Number(this.selectedPackages[item.id] || 0);
            if (!packageId) {
                return;
            }

            this.error = '';

            this.saving = {
                ...this.saving,
                [item.id]: true,
            };

            try {
                const response = await accountService.updateAdminAccountPackage(item.id, packageId);
                if (response && response.type === 'success') {
                    this.$toast.success('Paquete actualizado correctamente.');
                    await this.loadData();
                    return;
                }

                this.error = response?.message || 'No fue posible cambiar el paquete.';
            } catch (error) {
                this.error = error?.response?.data?.message || 'No fue posible cambiar el paquete.';
            } finally {
                this.saving = {
                    ...this.saving,
                    [item.id]: false,
                };
            }
        },
        getCurrentPeriod() {
            const now = new Date();
            const year = now.getFullYear();
            const month = String(now.getMonth() + 1).padStart(2, '0');

            return `${year}-${month}`;
        },
        buildAdminBackup(item) {
            const accessToken = String(localStorage.getItem('access_token') || '').trim();
            const rawUser = localStorage.getItem('user');

            if (!accessToken || !rawUser || rawUser === 'undefined') {
                return null;
            }

            let parsedUser = null;
            try {
                parsedUser = JSON.parse(rawUser) || null;
            } catch (_) {
                parsedUser = null;
            }

            if (!parsedUser || !parsedUser.is_admin) {
                return null;
            }

            return {
                admin_token: accessToken,
                admin_user: parsedUser,
                admin_rfcs: localStorage.getItem('rfcs') || '[]',
                admin_rfc: localStorage.getItem('rfc') || '',
                admin_fiscal_name: localStorage.getItem('fiscal_name') || '',
                admin_period: localStorage.getItem('period') || this.getCurrentPeriod(),
                started_at: new Date().toISOString(),
                target_account_id: Number(item?.id || 0) || null,
                target_account_name: String(item?.name || '').trim(),
            };
        },
        applyImpersonatedSession(payload) {
            const token = String(payload?.token || '').trim();
            const user = payload?.user || null;

            if (!token || !user) {
                throw new Error('No se recibió información de sesión para acceso temporal.');
            }

            localStorage.setItem('access_token', token);
            localStorage.setItem('user', JSON.stringify(user));
            localStorage.setItem('rfcs', JSON.stringify(user.rfcs || []));
            localStorage.setItem('period', localStorage.getItem('period') || this.getCurrentPeriod());

            if (user.rfc && user.rfc.rfc) {
                localStorage.setItem('rfc', user.rfc.rfc);
                localStorage.setItem('fiscal_name', user.rfc.name || '');
            } else {
                localStorage.setItem('rfc', '');
                localStorage.setItem('fiscal_name', '');
            }

            localStorage.setItem('admin_impersonation_context', JSON.stringify(payload?.impersonation || {}));
        },
        async impersonateAccount(item) {
            const accountId = Number(item?.id || 0);
            if (!accountId) {
                return;
            }

            this.error = '';
            this.impersonating = {
                ...this.impersonating,
                [accountId]: true,
            };

            try {
                const existingBackup = localStorage.getItem('admin_impersonation_backup');
                if (!existingBackup) {
                    const backup = this.buildAdminBackup(item);
                    if (backup) {
                        localStorage.setItem('admin_impersonation_backup', JSON.stringify(backup));
                    }
                }

                const response = await accountService.impersonateAdminAccount(accountId);
                if (response && response.type === 'success') {
                    this.applyImpersonatedSession(response.data || {});
                    this.$toast.success('Acceso temporal iniciado. Estás visualizando la cuenta seleccionada.');
                    await this.$router.push({ name: 'dashboard' });
                    return;
                }

                this.error = response?.message || 'No fue posible iniciar el acceso temporal.';
            } catch (error) {
                this.error = error?.response?.data?.message || 'No fue posible iniciar el acceso temporal.';
            } finally {
                this.impersonating = {
                    ...this.impersonating,
                    [accountId]: false,
                };
            }
        },
        async changeValidity(item){
            const endsAt = String(this.currentValidityDate(item) || '').trim();
            if (!endsAt) {
                return;
            }

            this.error = '';

            this.savingValidity = {
                ...this.savingValidity,
                [item.id]: true,
            };

            try {
                const response = await accountService.updateAdminAccountValidity(item.id, endsAt);
                if (response && response.type === 'success') {
                    const updatedEndsAt = response?.data?.ends_at || `${endsAt} 23:59:59`;
                    const rowIndex = this.rows.findIndex((row) => Number(row.id) === Number(item.id));
                    if (rowIndex >= 0) {
                        this.rows[rowIndex] = {
                            ...this.rows[rowIndex],
                            ends_at: updatedEndsAt,
                            next_payment_at: response?.data?.next_payment_at || null,
                            conekta_subscription_id: response?.data?.conekta_subscription_id || this.rows[rowIndex].conekta_subscription_id || null,
                        };
                    }
                    this.selectedValidityDates = {
                        ...this.selectedValidityDates,
                        [item.id]: this.toDateInput(updatedEndsAt),
                    };
                    this.$toast.success('Vigencia actualizada correctamente.');
                    return;
                }

                this.error = response?.message || 'No fue posible actualizar la vigencia.';
            } catch (error) {
                this.error = error?.response?.data?.message || 'No fue posible actualizar la vigencia.';
            } finally {
                this.savingValidity = {
                    ...this.savingValidity,
                    [item.id]: false,
                };
            }
        },
        async openDeleteModal(item){
            this.deleteModalVisible = true;
            this.deleteModalLoading = true;
            this.deleteModalDeleting = false;
            this.deleteModalError = '';
            this.deleteSummary = null;
            this.deleteTargetId = Number(item?.id || 0) || null;
            this.deleteConfirmationWord = '';

            if (!this.deleteTargetId) {
                this.deleteModalLoading = false;
                this.deleteModalError = 'No fue posible identificar la cuenta seleccionada.';
                return;
            }

            try {
                const response = await accountService.getAdminAccountDeletionSummary(this.deleteTargetId);
                if (response && response.type === 'success') {
                    this.deleteSummary = response?.data?.account || null;
                    return;
                }

                this.deleteModalError = response?.message || 'No fue posible cargar el resumen de eliminación.';
            } catch (error) {
                this.deleteModalError = error?.response?.data?.message || 'No fue posible cargar el resumen de eliminación.';
            } finally {
                this.deleteModalLoading = false;
            }
        },
        async openWhatsappConversationModal(item) {
            const accountId = Number(item?.id || 0);
            if (!accountId) {
                return;
            }

            this.whatsappConversationVisible = true;
            this.whatsappConversationLoading = true;
            this.whatsappConversationError = '';
            this.whatsappConversationMessages = [];
            this.whatsappConversationAccount = {
                id: accountId,
                name: item?.name || '',
                email: item?.email || '',
            };
            this.whatsappConversationContact = {
                phone_number: item?.whatsapp_phone_number || '',
            };
            this.whatsappConversationAccountId = accountId;
            this.whatsappConversationHasMore = false;
            this.whatsappConversationNextBeforeId = null;
            this.whatsappConversationLoadingOlder = false;
            this.whatsappReplyDraft = '';

            await this.loadWhatsappConversation({ accountId });
        },
        closeWhatsappConversationModal() {
            this.whatsappConversationVisible = false;
            this.whatsappConversationLoading = false;
            this.whatsappConversationError = '';
            this.whatsappConversationAccount = null;
            this.whatsappConversationContact = null;
            this.whatsappConversationMessages = [];
            this.whatsappConversationAccountId = null;
            this.whatsappConversationHasMore = false;
            this.whatsappConversationNextBeforeId = null;
            this.whatsappConversationLoadingOlder = false;
            this.whatsappReplyDraft = '';
            this.whatsappReplySending = false;
            this.whatsappCanSendDirect = false;
            this.whatsappTemplateOptions = [];
            this.whatsappTemplateSelected = '';
            this.whatsappTemplateSending = false;
        },
        async loadWhatsappConversation({ accountId, beforeId = null, appendOlder = false }) {
            if (appendOlder) {
                this.whatsappConversationLoadingOlder = true;
            } else {
                this.whatsappConversationLoading = true;
            }
            this.whatsappConversationError = '';

            try {
                const response = await accountService.getAdminAccountWhatsappConversation(accountId, {
                    limit: 40,
                    beforeId,
                });
                if (response && response.type === 'success') {
                    this.whatsappConversationAccount = response?.data?.account || this.whatsappConversationAccount;
                    this.whatsappConversationContact = response?.data?.contact || this.whatsappConversationContact;
                    const receivedMessages = Array.isArray(response?.data?.messages)
                        ? response.data.messages
                        : [];
                    if (appendOlder) {
                        this.whatsappConversationMessages = [
                            ...receivedMessages,
                            ...this.whatsappConversationMessages,
                        ];
                    } else {
                        this.whatsappConversationMessages = receivedMessages;
                    }

                    this.whatsappConversationHasMore = Boolean(response?.data?.pagination?.has_more);
                    this.whatsappConversationNextBeforeId = response?.data?.pagination?.next_before_id || null;
                    this.whatsappCanSendDirect = Boolean(response?.data?.capabilities?.can_send_direct);
                    this.whatsappTemplateOptions = Array.isArray(response?.data?.templates)
                        ? response.data.templates
                        : [];

                    return;
                }

                this.whatsappConversationError = response?.message || 'No fue posible cargar la conversación de WhatsApp.';
            } catch (error) {
                this.whatsappConversationError = error?.response?.data?.message || 'No fue posible cargar la conversación de WhatsApp.';
            } finally {
                if (appendOlder) {
                    this.whatsappConversationLoadingOlder = false;
                } else {
                    this.whatsappConversationLoading = false;
                }
            }
        },
        async loadOlderWhatsappMessages() {
            const accountId = Number(this.whatsappConversationAccountId || 0);
            const beforeId = Number(this.whatsappConversationNextBeforeId || 0);

            if (!accountId || !beforeId || this.whatsappConversationLoadingOlder) {
                return;
            }

            await this.loadWhatsappConversation({
                accountId,
                beforeId,
                appendOlder: true,
            });
        },
        async sendWhatsappReply() {
            if (!this.canSendWhatsappReply) {
                return;
            }

            const accountId = Number(this.whatsappConversationAccountId || 0);
            const message = String(this.whatsappReplyDraft || '').trim();
            if (!accountId || !message) {
                return;
            }

            this.whatsappReplySending = true;
            this.whatsappConversationError = '';

            try {
                const response = await accountService.sendAdminAccountWhatsappReply(accountId, message);
                if (response && response.type === 'success') {
                    this.whatsappReplyDraft = '';
                    this.$toast.success('Mensaje enviado por WhatsApp correctamente.');
                    await this.loadWhatsappConversation({ accountId });
                    return;
                }

                this.whatsappConversationError = response?.message || 'No fue posible enviar el mensaje por WhatsApp.';
            } catch (error) {
                this.whatsappConversationError = error?.response?.data?.message || 'No fue posible enviar el mensaje por WhatsApp.';
            } finally {
                this.whatsappReplySending = false;
            }
        },
        async sendWhatsappTemplate() {
            if (!this.canSendWhatsappTemplate) {
                return;
            }

            const accountId = Number(this.whatsappConversationAccountId || 0);
            const templateKey = String(this.whatsappTemplateSelected || '').trim();
            if (!accountId || !templateKey) {
                return;
            }

            this.whatsappTemplateSending = true;
            this.whatsappConversationError = '';

            try {
                const response = await accountService.sendAdminAccountWhatsappTemplate(accountId, templateKey);
                if (response && response.type === 'success') {
                    this.$toast.success('Template enviado por WhatsApp correctamente.');
                    await this.loadWhatsappConversation({ accountId });
                    return;
                }

                this.whatsappConversationError = response?.message || 'No fue posible enviar el template por WhatsApp.';
            } catch (error) {
                this.whatsappConversationError = error?.response?.data?.message || 'No fue posible enviar el template por WhatsApp.';
            } finally {
                this.whatsappTemplateSending = false;
            }
        },
        openPasswordModal(item) {
            const accountId = Number(item?.id || 0);
            if (!accountId) {
                return;
            }

            this.passwordModalVisible = true;
            this.passwordModalLoading = false;
            this.passwordModalGenerating = false;
            this.passwordModalError = '';
            this.passwordModalGeneratedPassword = '';
            this.passwordModalGeneratedUser = null;
            this.passwordModalTarget = {
                id: accountId,
                name: item?.name || '',
                email: item?.email || '',
            };
        },
        closePasswordModal() {
            if (this.passwordModalGenerating) {
                return;
            }

            this.passwordModalVisible = false;
            this.passwordModalLoading = false;
            this.passwordModalGenerating = false;
            this.passwordModalError = '';
            this.passwordModalTarget = null;
            this.passwordModalGeneratedPassword = '';
            this.passwordModalGeneratedUser = null;
        },
        async generatePasswordForAccount() {
            const accountId = Number(this.passwordModalTarget?.id || 0);
            if (!accountId) {
                return;
            }

            this.passwordModalGenerating = true;
            this.passwordModalError = '';

            try {
                const response = await accountService.generateAdminAccountUserPassword(accountId);
                if (response && response.type === 'success') {
                    this.passwordModalGeneratedPassword = String(response?.data?.generated_password || '');
                    this.passwordModalGeneratedUser = response?.data?.user || null;
                    return;
                }

                this.passwordModalError = response?.message || 'No fue posible generar la contraseña.';
            } catch (error) {
                this.passwordModalError = error?.response?.data?.message || 'No fue posible generar la contraseña.';
            } finally {
                this.passwordModalGenerating = false;
            }
        },
        closeDeleteModal(){
            this.deleteModalVisible = false;
            this.deleteModalLoading = false;
            this.deleteModalDeleting = false;
            this.deleteModalError = '';
            this.deleteSummary = null;
            this.deleteTargetId = null;
            this.deleteConfirmationWord = '';
        },
        isDeletionWordValid(){
            return String(this.deleteConfirmationWord || '').trim() === 'ELIMINAR';
        },
        async confirmDeleteAccount(){
            if (!this.deleteTargetId || !this.isDeletionWordValid()) {
                return;
            }

            this.deleteModalError = '';
            this.deleteModalDeleting = true;
            this.error = '';

            try {
                const response = await accountService.deleteAdminAccount(this.deleteTargetId, this.deleteConfirmationWord);
                if (response && response.type === 'success') {
                    this.$toast.success('Cuenta eliminada correctamente.');
                    this.closeDeleteModal();
                    await this.loadData();
                    return;
                }

                this.deleteModalError = response?.message || 'No fue posible eliminar la cuenta.';
            } catch (error) {
                this.deleteModalError = error?.response?.data?.message || 'No fue posible eliminar la cuenta.';
            } finally {
                this.deleteModalDeleting = false;
            }
        },
        packageLabel(pkg){
            const limit = pkg?.rfc_limit === null || pkg?.rfc_limit === undefined
                ? 'RFCs ilimitados'
                : `${pkg.rfc_limit} RFC${Number(pkg.rfc_limit) === 1 ? '' : 's'}`;

            return `${pkg?.name || 'Paquete'} - ${limit}`;
        },
        packageMeta(item){
            const limit = item?.package_rfc_limit;
            if (limit === null || limit === undefined) {
                return 'RFCs ilimitados';
            }

            return `${limit} RFC${Number(limit) === 1 ? '' : 's'}`;
        },
        formatDate(value){
            if(!value) return 'Sin fecha';

            const date = new Date(value);
            if (Number.isNaN(date.getTime())) return 'Sin fecha';

            return date.toLocaleDateString('es-MX', {
                year: 'numeric',
                month: 'short',
                day: '2-digit'
            });
        },
        formatDateTime(value){
            if(!value) return '-';

            const date = new Date(value);
            if (Number.isNaN(date.getTime())) return '-';

            return date.toLocaleString('es-MX', {
                year: 'numeric',
                month: '2-digit',
                day: '2-digit',
                hour: '2-digit',
                minute: '2-digit',
                second: '2-digit',
                hour12: false,
            });
        },
        toDateInput(value){
            if (!value) return '';

            const raw = String(value).trim();
            const directMatch = raw.match(/^(\d{4}-\d{2}-\d{2})/);
            if (directMatch) {
                return directMatch[1];
            }

            const date = new Date(raw);
            if (Number.isNaN(date.getTime())) return '';

            const year = date.getUTCFullYear();
            const month = String(date.getUTCMonth() + 1).padStart(2, '0');
            const day = String(date.getUTCDate()).padStart(2, '0');
            return `${year}-${month}-${day}`;
        },
        currentValidityDate(item){
            const typedValue = this.selectedValidityDates[item.id];
            if (typedValue) {
                return typedValue;
            }

            return this.toDateInput(item?.ends_at || null);
        },
        onValidityInput(accountId, value){
            this.selectedValidityDates = {
                ...this.selectedValidityDates,
                [accountId]: value,
            };
        },
        formatNextPayment(item){
            if (!item?.conekta_subscription_id) {
                return '-';
            }

            return this.formatDate(item.next_payment_at);
        },
        providerStatusLabel(hasRegistered, hasLogin){
            if (hasRegistered && hasLogin) {
                return 'registro + login';
            }

            if (hasRegistered) {
                return 'registro';
            }

            if (hasLogin) {
                return 'login';
            }

            return '';
        },
        socialBadges(item){
            const hasGoogleLogin = Boolean(item?.has_google_login_user);
            const hasFacebookLogin = Boolean(item?.has_facebook_login_user);
            const googleStatus = this.providerStatusLabel(
                Boolean(item?.has_google_registered_user),
                hasGoogleLogin
            );
            const facebookStatus = this.providerStatusLabel(
                Boolean(item?.has_facebook_registered_user),
                hasFacebookLogin
            );

            const badges = [];

            if (hasGoogleLogin && hasFacebookLogin) {
                badges.push({
                    type: 'both',
                    text: 'Login en ambos',
                });
            }

            if (googleStatus) {
                badges.push({
                    type: 'google',
                    text: `Google: ${googleStatus}`,
                });
            }

            if (facebookStatus) {
                badges.push({
                    type: 'facebook',
                    text: `Facebook: ${facebookStatus}`,
                });
            }

            return badges;
        },
        hasRecentWhatsappMessage(item) {
            const value = String(item?.whatsapp_last_message_at || '').trim();
            if (!value) {
                return false;
            }

            const messageDate = new Date(value);
            if (Number.isNaN(messageDate.getTime())) {
                return false;
            }

            const now = Date.now();
            const diffMs = now - messageDate.getTime();
            const dayMs = 24 * 60 * 60 * 1000;

            return diffMs >= 0 && diffMs <= dayMs;
        },
        whatsappButtonClass(item) {
            return this.hasRecentWhatsappMessage(item)
                ? 'btn-success'
                : 'btn-light whatsapp-btn-neutral';
        },
        whatsappIconClass(item) {
            return this.hasRecentWhatsappMessage(item)
                ? 'text-white'
                : 'text-success';
        },
        whatsappStatusMeta(message) {
            const status = String(message?.message_status || '').trim().toLowerCase();

            // Twilio outbound lifecycle mapped to familiar WhatsApp-like indicators.
            if (status === 'read') {
                return {
                    kind: 'ticks',
                    ticksText: '✓✓',
                    color: '#3b82f6',
                    label: 'Leido',
                };
            }

            if (status === 'delivered') {
                return {
                    kind: 'ticks',
                    ticksText: '✓✓',
                    color: '#6b7280',
                    label: 'Entregado',
                };
            }

            if (status === 'sent') {
                return {
                    kind: 'ticks',
                    ticksText: '✓',
                    color: '#6b7280',
                    label: 'Enviado',
                };
            }

            if (status === 'failed' || status === 'undelivered') {
                return {
                    kind: 'icon',
                    iconClass: 'fa-exclamation-circle',
                    color: '#dc2626',
                    label: 'Error',
                };
            }

            if (['queued', 'accepted', 'scheduled', 'sending', 'created'].includes(status)) {
                return {
                    kind: 'icon',
                    iconClass: 'fa-clock-o',
                    color: '#6b7280',
                    label: 'En proceso',
                };
            }

            return {
                kind: 'icon',
                iconClass: 'fa-clock-o',
                color: '#6b7280',
                label: status ? `Estado: ${status}` : 'En proceso',
            };
        }
    },
    computed: {
        filteredRows() {
            const query = String(this.searchQuery || '').trim().toLowerCase();
            let filtered = this.rows.filter((item) => {
                if (!query) {
                    return true;
                }

                const name = String(item?.name || '').toLowerCase();
                const email = String(item?.email || '').toLowerCase();
                const whatsapp = String(item?.whatsapp_phone_number || '').toLowerCase();
                return name.includes(query) || email.includes(query) || whatsapp.includes(query);
            });

            filtered = [...filtered].sort((a, b) => {
                const aCreatedAt = new Date(a?.created_at || '').getTime();
                const bCreatedAt = new Date(b?.created_at || '').getTime();

                const aValid = Number.isFinite(aCreatedAt);
                const bValid = Number.isFinite(bCreatedAt);

                if (aValid && bValid && aCreatedAt !== bCreatedAt) {
                    return bCreatedAt - aCreatedAt;
                }

                return Number(b?.id || 0) - Number(a?.id || 0);
            });

            return filtered;
        },
        canSendWhatsappReply() {
            const accountId = Number(this.whatsappConversationAccountId || 0);
            const message = String(this.whatsappReplyDraft || '').trim();
            return accountId > 0
                && this.whatsappCanSendDirect
                && message.length > 0
                && message.length <= 1500;
        },
        canSendWhatsappTemplate() {
            const accountId = Number(this.whatsappConversationAccountId || 0);
            const templateKey = String(this.whatsappTemplateSelected || '').trim();
            return accountId > 0 && templateKey !== '';
        }
    }
}
</script>

<style scoped>
.admin-header{
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 12px;
}

.admin-subtitle{
    margin: 4px 0 0;
    font-size: 12px;
    color: #6c757d;
}

.admin-search-wrap {
    max-width: 360px;
}


.admin-accounts-table .btn.whatsapp-btn-neutral {
    border: 1px solid #22c55e !important;
    background-color: #ffffff !important;
    color: #16a34a !important;
}

.admin-accounts-table .btn.whatsapp-btn-neutral i {
    color: #16a34a !important;
}

.account-email {
    text-transform: lowercase !important;
}

.admin-accounts-table th {
    font-size: 11px;
    padding: 6px 8px !important;
    white-space: nowrap;
}

.admin-accounts-table td {
    font-size: 11px;
    padding: 6px 8px !important;
}

.admin-accounts-table .fw-bold {
    font-size: 12px;
    line-height: 1.2;
}

.admin-accounts-table .account-email {
    font-size: 10px;
}

.admin-accounts-table .account-whatsapp {
    display: block;
    font-size: 10px;
    line-height: 1.2;
}

.admin-accounts-table .badge {
    font-size: 10px;
}

.admin-accounts-table .social-badge {
    display: inline-block;
    border: 1px solid transparent;
    font-weight: 600;
}

.admin-accounts-table .social-badges-group {
    display: flex;
    flex-wrap: wrap;
    gap: 4px;
}

.admin-accounts-table .social-badge-both {
    background-color: #f3f4f6 !important;
    color: #374151 !important;
    border-color: #d1d5db;
}

.admin-accounts-table .social-badge-google {
    background-color: #fde8e8 !important;
    color: #991b1b !important;
    border-color: #f8b4b4;
}

.admin-accounts-table .social-badge-facebook {
    background-color: #e8f1ff !important;
    color: #0d3b78 !important;
    border-color: #b6ccf3;
}

.admin-accounts-table .btn {
    font-size: 10px;
    line-height: 1.2;
    padding: 3px 6px;
}

.admin-accounts-table .account-actions .btn {
    white-space: nowrap;
}

.admin-accounts-table .form-select,
.admin-accounts-table .form-control {
    font-size: 10px;
    line-height: 1.2;
    padding: 3px 6px;
    min-height: 28px;
}

.whatsapp-conversation-modal {
    min-height: 70vh;
}

.whatsapp-conversation-account {
    font-size: 13px;
    color: #4b5563;
}

.whatsapp-conversation-list {
    border: 1px solid #e5e7eb;
    border-radius: 10px;
    background: #f8fafc;
    max-height: 52vh;
    overflow-y: auto;
    padding: 12px;
}

.whatsapp-message-row {
    display: flex;
    margin-bottom: 10px;
}

.whatsapp-message-row.is-inbound {
    justify-content: flex-start;
}

.whatsapp-message-row.is-outbound {
    justify-content: flex-end;
}

.whatsapp-message-bubble {
    max-width: 78%;
    border-radius: 10px;
    padding: 10px;
    border: 1px solid #d1d5db;
    background: #ffffff;
}

.whatsapp-message-row.is-outbound .whatsapp-message-bubble {
    background: #dcfce7;
    border-color: #86efac;
}

.whatsapp-message-meta {
    display: flex;
    justify-content: space-between;
    gap: 8px;
    font-size: 11px;
    color: #6b7280;
    margin-bottom: 5px;
}

.whatsapp-message-body {
    white-space: pre-wrap;
    font-size: 13px;
    color: #111827;
}

.whatsapp-message-status {
    margin-top: 5px;
    font-size: 10px;
    color: #6b7280;
    display: inline-flex;
    align-items: center;
    gap: 6px;
}

.whatsapp-status-ticks {
    font-size: 12px;
    font-weight: 700;
    line-height: 1;
    letter-spacing: -1px;
}

.whatsapp-reply-actions {
    display: flex;
    align-items: center;
    justify-content: space-between;
}

</style>
